/* global Office */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CreateRFQDialog from "@/pages/CreateRFQDialog";
import OutlookQuoteFlow from "@/components/OutlookQuoteFlow";
import { Loader2, Mail, CheckCircle2, ExternalLink, RotateCcw, Copy } from "lucide-react";
import { toast } from "sonner";

const OFFICE_JS = "https://appsforoffice.microsoft.com/lib/1/hosted/office.js";
const KIND = (n, t) => (/pdf/i.test(t) || /\.pdf$/i.test(n) ? "pdf" : /^image\//i.test(t) ? "image" : /\.xlsx?$/i.test(n) ? "excel" : null);

let officePromise = null;
const inOffice = () => /_host_Info=/i.test(window.location.search + window.location.hash) || window.self !== window.top || !!window.chrome?.webview;

function loadOffice() {
  if (!inOffice()) return Promise.resolve(null);
  officePromise = officePromise || new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = OFFICE_JS;
    s.onload = () => window.Office.onReady(resolve);
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
  return officePromise;
}

const call = (fn) => new Promise((res) => fn((r) => res(r.status === "succeeded" ? r.value : null)));

async function readEmail() {
  const item = window.Office.context?.mailbox?.item;
  if (!item) return null;
  const body = (await call((cb) => item.body.getAsync(window.Office.CoercionType.Text, cb))) || "";
  const from = item.from ? `${item.from.displayName} <${item.from.emailAddress}>` : "";
  const when = item.dateTimeCreated ? new Date(item.dateTimeCreated).toISOString().slice(0, 10) : "";
  const text = `From: ${from}\nSubject: ${item.subject || ""}\nEmail Sent Date: ${when}\n\n${body}`.slice(0, 20000);
  const atts = (item.attachments || []).filter((a) => !a.isInline && a.attachmentType !== "item" && (a.size || 0) <= 15 * 1024 * 1024).slice(0, 15);
  const files = [];
  if (item.getAttachmentContentAsync) {
    for (const att of atts) {
      const c = await call((cb) => item.getAttachmentContentAsync(att.id, cb));
      if (c?.format !== "base64") continue;
      const bin = atob(c.content);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      files.push(new File([bytes], att.name, { type: att.contentType || "application/octet-stream" }));
    }
  }
  // The first PDF (else image / Excel) is read by the AI; every other attachment is saved on the RFQ.
  const rank = (f) => ({ pdf: 0, image: 1, excel: 2 }[KIND(f.name, f.type)] ?? 9);
  const file = [...files].sort((a, b) => rank(a) - rank(b)).find((f) => KIND(f.name, f.type)) || null;
  const extras = files.filter((f) => f !== file);
  return { subject: item.subject, from, fromEmail: item.from?.emailAddress || "", to: (item.to || []).map((t) => t.emailAddress).join(", "), sent: when, body, source: file ? KIND(file.name, file.type) : "text", text, file, extras, skipped: (item.attachments || []).filter((a) => !a.isInline).length - files.length };
}

function LoginBox() {
  const { login } = useAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await login(f.email, f.password); } catch { setErr("Incorrect Email Or Password"); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={go} className="space-y-3" data-testid="addin-login">
      <p className="text-sm text-slate-600">Sign In To Somcore Hub Once — You'll Stay Signed In.</p>
      <Input type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="addin-email" />
      <Input type="password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="addin-password" />
      {err && <p className="text-xs text-red-600">{err}</p>}
      <Button type="submit" disabled={busy} className="w-full bg-[#1B4383] hover:bg-[#16386e]" data-testid="addin-login-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Sign In</Button>
    </form>
  );
}

export const AckDraft = ({ rfqId }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/rfqs/${rfqId}/acknowledge/draft`).then((r) => setD(r.data)).catch(() => {}); }, [rfqId]);
  if (!d) return null;
  const copy = async (txt, what) => { try { await navigator.clipboard.writeText(txt); toast.success(`${what} Copied`); } catch { window.prompt(`Copy The ${what}`, txt); } };
  return (
    <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-left" data-testid="addin-ack-draft">
      <div className="text-xs font-bold text-amber-900">Acknowledgement Draft — Copy & Reply To The Client</div>
      <div className="flex items-center gap-2 text-xs"><span className="min-w-0 flex-1 truncate"><b>Subject:</b> {d.subject}</span><button className="shrink-0 font-semibold text-[#1B4383]" onClick={() => copy(d.subject, "Subject")} data-testid="addin-ack-copy-subject">Copy</button></div>
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded border border-amber-100 bg-white p-2 text-[11px] text-slate-700" data-testid="addin-ack-message">{d.message}</pre>
      <Button size="sm" className="w-full bg-[#A87F31] hover:bg-[#8f6b28]" onClick={() => copy(d.message, "Message")} data-testid="addin-ack-copy"><Copy className="mr-1 h-4 w-4" />Copy Message</Button>
    </div>
  );
};

export default function OutlookAddin() {
  const { user } = useAuth();
  const [state, setState] = useState("loading");
  const [email, setEmail] = useState(null);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState(null);
  const [mode, setMode] = useState("rfq");
  const [prefill, setPrefill] = useState(null);
  const [preparing, setPreparing] = useState(false);

  useEffect(() => {
    loadOffice().then(async (info) => {
      if (!info || !window.Office?.context?.mailbox) { setState("browser"); return; }
      setEmail(await readEmail());
      setState("ready");
    });
  }, []);
  useEffect(() => { if (user) api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, [user]);

  const start = async () => {
    setCreated(null);
    let p = email;
    if (!email.file) {
      setPreparing(true);
      try {
        const { data } = await api.post("/outlook/email-pdf", { subject: email.subject, sender: email.from, sent: email.sent, to: email.to, body: email.body });
        const bin = atob(data.file_base64); const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        p = { ...email, source: "pdf", file: new File([bytes], data.filename, { type: "application/pdf" }) };
      } catch { p = email; } finally { setPreparing(false); }
    }
    setPrefill(p); setOpen(true);
  };
  return (
    <div className="min-h-screen bg-[#F4F7FC] p-4" data-testid="outlook-addin">
      <div className="mb-4 flex items-center gap-2"><img src="/apple-touch-icon.png" alt="" className="h-8 w-8 rounded-md" /><div className="font-heading text-base font-bold text-[#12294B]">Somcore Hub</div></div>
      <div className="rounded-xl border border-border bg-white p-4">
        {state === "loading" || user === null ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          : state === "browser" ? <p className="text-sm text-slate-600" data-testid="addin-browser">This Page Runs Inside Outlook. Open An Email In Outlook And Click <b>Add To Somcore Hub</b>.</p>
          : !user ? <LoginBox />
          : created ? (
            <div className="space-y-3 text-center" data-testid="addin-created">
              <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600" />
              <div className="font-semibold text-slate-800">RFQ {created.code} Created</div>
              <AckDraft rfqId={created.id} />
              <Button className="w-full bg-[#1B4383] hover:bg-[#16386e]" onClick={() => window.open(`${window.location.origin}/rfqs/${created.id}`, "_blank")} data-testid="addin-open-rfq"><ExternalLink className="mr-2 h-4 w-4" />Open In Somcore Hub</Button>
              <Button variant="outline" className="w-full" onClick={start}><RotateCcw className="mr-2 h-4 w-4" />Start Again</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-border text-xs font-semibold" data-testid="addin-mode">
                {[["rfq", "New RFQ"], ["quote", "Supplier Quote"]].map(([k, l]) => <button key={k} onClick={() => setMode(k)} className={`py-2 ${mode === k ? "bg-[#1B4383] text-white" : "bg-white text-slate-600"}`} data-testid={`addin-mode-${k}`}>{l}</button>)}
              </div>
              <div className="flex items-start gap-2 text-sm"><Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#1B4383]" /><div className="min-w-0"><div className="truncate font-semibold text-slate-800">{email?.subject || "(No Subject)"}</div><div className="truncate text-xs text-muted-foreground">{email?.from}</div></div></div>
              {mode === "quote" ? <OutlookQuoteFlow email={email} /> : <>
              <div className="text-xs text-slate-600" data-testid="addin-attachment">
                {email?.file ? <p>AI Reads: <b>{email.file.name}</b> + The Email Text.</p> : <p>No Attachment — The Email Itself Is Saved As A PDF And Used As The RFQ Document.</p>}
                {email?.extras?.length > 0 && <p className="mt-1">Also Saved On The RFQ: {email.extras.map((f) => f.name).join(", ")}</p>}
                {email?.skipped > 0 && <p className="mt-1 text-amber-700">{email.skipped} Attachment(s) Too Large Or Not A File — Add Them From The RFQ Page.</p>}
              </div>
              <Button className="w-full bg-[#1B4383] hover:bg-[#16386e]" onClick={start} disabled={preparing} data-testid="addin-start">{preparing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create RFQ From This Email</Button>
              </>}
            </div>
          )}
      </div>
      {user && email && (
        <CreateRFQDialog open={open} onOpenChange={setOpen} clients={clients} prefill={open ? prefill : null}
          onClientCreated={(c) => setClients((p) => [...p, c])} onCreated={setCreated} />
      )}
    </div>
  );
}
